import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
 import {
   ArrowLeftIcon,
   MagnifyingGlassIcon,
   PlusIcon,
   ArrowPathRoundedSquareIcon,
   MinusCircleIcon,
   PlusCircleIcon,
   ClipboardDocumentCheckIcon,
   TrashIcon
 } from "@heroicons/react/24/outline";

const API_BASE = "https://bayautomation-qctracker.co.za/api";

const authHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fetchJSON = async (url, init = {}) => {
  const res = await fetch(url, init);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} – ${text}`);
  }
  return res.json();
};

export default function StockTakePage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  useEffect(() => {
    if (!token) navigate("/", { replace: true });
  }, [token, navigate]);

  // data
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // add item form
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [unit, setUnit] = useState("pcs");
  const [reorderLevel, setReorderLevel] = useState(10);
  const [initialQty, setInitialQty] = useState(0);

  // ui
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await fetchJSON(`${API_BASE}/stock/items`, {
        headers: { ...authHeaders() },
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load stock items:", e);
      alert("Failed to load stock items (see console).");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.name?.toLowerCase().includes(q) ||
        it.sku?.toLowerCase().includes(q) ||
        it.unit?.toLowerCase().includes(q)
    );
  }, [items, search]);

  const lowStockCount = filtered.filter(
    (it) => Number(it.quantity) <= Number(it.reorderLevel)
  ).length;

  // actions
  const addItem = async () => {
    if (!name.trim() || !sku.trim()) {
      alert("Name and SKU are required");
      return;
    }
    setAdding(true);
    try {
      const payload = {
        name: name.trim(),
        sku: sku.trim(),
        unit: unit.trim() || "pcs",
        reorderLevel: Number(reorderLevel) || 0,
        quantity: Number(initialQty) || 0,
      };
      await fetchJSON(`${API_BASE}/stock/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      });
      setName("");
      setSku("");
      setUnit("pcs");
      setReorderLevel(10);
      setInitialQty(0);
      await loadItems();
    } catch (e) {
      console.error("Add item failed:", e);
      alert("Failed to add item.");
    } finally {
      setAdding(false);
    }
  };

  const adjustStock = async (item, isIncrease = false) => {
    const sign = isIncrease ? "+" : "−";
    const qtyStr = prompt(
      `Enter quantity to ${isIncrease ? "add" : "subtract"} for ${item.name} (${item.sku}):`
    );
    if (qtyStr == null) return;
    const qty = Number(qtyStr);
    if (!Number.isFinite(qty) || qty <= 0) {
      alert("Please enter a positive number.");
      return;
    }
    const note = prompt("Optional note for this adjustment:") || "";

    try {
      await fetchJSON(`${API_BASE}/stock/adjust`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          itemId: item.id,
          delta: isIncrease ? qty : -qty,
          note,
        }),
      });
      await loadItems();
    } catch (e) {
      console.error("Adjust failed:", e);
      alert("Failed to adjust stock.");
    }
  };

  const consumeForPart = async (item) => {
    const partID = prompt(`Enter Part ID to consume ${item.name} for:`);
    if (!partID) return;
    const qtyStr = prompt(`Quantity of ${item.name} to use for Part ${partID}:`);
    if (qtyStr == null) return;
    const qty = Number(qtyStr);
    if (!Number.isFinite(qty) || qty <= 0) {
      alert("Please enter a positive number.");
      return;
    }

    try {
      await fetchJSON(`${API_BASE}/stock/consume`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          itemId: item.id,
          quantity: qty,
          partID: String(partID).trim(),
        }),
      });
      await loadItems();
    } catch (e) {
      console.error("Consume failed:", e);
      alert("Failed to consume stock for part.");
    }
  };

  const exportCSV = () => {
    const headers = ["Name", "SKU", "Unit", "Quantity", "ReorderLevel", "LastUpdated"];
    const rows = filtered.map((it) => [
      escapeCsv(it.name),
      escapeCsv(it.sku),
      escapeCsv(it.unit),
      it.quantity ?? 0,
      it.reorderLevel ?? 0,
      it.lastUpdated ?? "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stock-take-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Stock Take</h1>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCSV} title="Export CSV">
            <ArrowPathRoundedSquareIcon className="h-4 w-4 mr-2" />
            Export
          </Button>
          <div className="relative">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-2 top-2.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name/SKU/unit…"
              className="pl-8 pr-3 py-2 border rounded w-64"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 border rounded-xl bg-white">
          <div className="text-sm text-gray-500">Items</div>
          <div className="text-2xl font-semibold">{filtered.length}</div>
        </div>
        <div className="p-4 border rounded-xl bg-white">
          <div className="text-sm text-gray-500">Low / At Reorder</div>
          <div className={`text-2xl font-semibold ${lowStockCount ? "text-red-600" : ""}`}>
            {lowStockCount}
          </div>
        </div>
        <div className="p-4 border rounded-xl bg-white">
          <div className="text-sm text-gray-500">Last Updated</div>
          <div className="text-2xl font-semibold">
            {new Date().toLocaleString()}
          </div>
        </div>
      </div>

      {/* Add item */}
      <div className="p-4 border rounded-xl bg-white space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Add New Item</h2>
        </div>
        <div className="grid md:grid-cols-6 gap-3">
          <input
            className="border rounded p-2 md:col-span-2"
            placeholder="Item name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="border rounded p-2"
            placeholder="SKU *"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
          />
          <input
            className="border rounded p-2"
            placeholder="Unit (e.g. pcs)"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          />
          <input
            type="number"
            className="border rounded p-2"
            placeholder="Reorder level"
            value={reorderLevel}
            onChange={(e) => setReorderLevel(e.target.value)}
          />
          <input
            type="number"
            className="border rounded p-2"
            placeholder="Initial quantity"
            value={initialQty}
            onChange={(e) => setInitialQty(e.target.value)}
          />
        </div>
        <div className="flex items-center justify-end">
          <Button onClick={addItem} disabled={adding}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {adding ? "Adding…" : "Add Item"}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto border rounded-xl bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 border text-left">Name</th>
              <th className="px-4 py-2 border text-left">SKU</th>
              <th className="px-4 py-2 border text-left">Unit</th>
              <th className="px-4 py-2 border text-right">In Stock</th>
              <th className="px-4 py-2 border text-right">Reorder</th>
              <th className="px-4 py-2 border text-left">Last Updated</th>
              <th className="px-4 py-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-4 py-6 text-center" colSpan={7}>
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={7}>
                  No items found.
                </td>
              </tr>
            ) : (
              filtered.map((it) => {
                const low = Number(it.quantity) <= Number(it.reorderLevel);
                return (
                  <tr key={it.id} className={low ? "bg-red-50" : ""}>
                    <td className="px-4 py-2 border font-medium">{it.name}</td>
                    <td className="px-4 py-2 border">{it.sku}</td>
                    <td className="px-4 py-2 border">{it.unit}</td>
                    <td className="px-4 py-2 border text-right">{it.quantity ?? 0}</td>
                    <td className="px-4 py-2 border text-right">{it.reorderLevel ?? 0}</td>
                    <td className="px-4 py-2 border">
                      {it.lastUpdated
                        ? new Date(it.lastUpdated).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-4 py-2 border">
                      <div className="flex flex-wrap gap-2 justify-center">
                        <Button variant="outline" onClick={() => adjustStock(it, true)} title="Add">
                          <PlusCircleIcon className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                        <Button variant="outline" onClick={() => adjustStock(it, false)} title="Subtract">
                          <MinusCircleIcon className="h-4 w-4 mr-1" />
                          Subtract
                        </Button>
                        <Button onClick={() => consumeForPart(it)} title="Use for Part">
                          <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1" />
                          Use for Part
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// small helper
function escapeCsv(val) {
  const s = String(val ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
